import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';

// Sourced from `.env.development` / `.env.production` (Vite picks the file
// by mode: `npm run dev` loads .development, `npm run build` loads
// .production) so the console points at hatod-dev vs hatod-72607 without
// a code change. Web API keys are not secrets — they're scoped via
// Firebase project rules + auth.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);

/**
 * Opt-in local Firebase, matching the Flutter app's emulator switch.
 *
 * Vite only exposes `VITE_*` variables to browser code, and the dev-mode
 * check makes it impossible for a production build to point the ops console
 * at localhost even if its environment is misconfigured. Keeping this switch
 * here, next to client creation, also guarantees Auth and Firestore move as a
 * pair — a staff token minted by the emulator is useless against live rules,
 * and mixing the two produces a particularly confusing sign-in loop.
 */
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  const host = import.meta.env.VITE_FIREBASE_EMULATOR_HOST || '127.0.0.1';
  const authPort = numberEnv(import.meta.env.VITE_AUTH_EMULATOR_PORT, 9099);
  const firestorePort = numberEnv(
    import.meta.env.VITE_FIRESTORE_EMULATOR_PORT,
    8080,
  );

  connectAuthEmulator(auth, `http://${host}:${authPort}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, host, firestorePort);
}

function numberEnv(raw: string | undefined, fallback: number): number {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 && parsed <= 65_535
    ? parsed
    : fallback;
}
